import { useRef, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useDropzone } from "react-dropzone";
import {
  setDisableNextButton,
  setFileUploaded,
  setFileUploadPageName,
} from "../../slices/adminPageSlice.js";
import { setImage } from "../../slices/imageSlice.js";
import {
  deleteCuratorFile,
  downloadFileToLocal,
  uploadYearlyReviewArtificatsFiles,
} from "../../API/curatorApi.js";
import { uploadThumbnailAndGiveFileId } from "../../utils/function.js";
import { showErrorToast } from "../../utils/Toast/toast";
import useThemeRender from "../../theme/useThemeRender";

/**
 * Shared upload/validation/abort/retry/delete logic behind StepOneForm (internal admin
 * upload) and ExternalStepOneForm (external/public upload).
 */
export const useStepOneUpload = ({
  formData,
  setFormData,
  setFileType,
  fileDocument,
  setFileDocumentAction,
  fileUploadPageName,
  hasContentTypeSyncEffect = false,
  parseFileIdAsInt = false,
  thumbnailValidation = true,
  awaitDocumentDelete = false,
  logFileUploadedEffect = false,
}) => {
  const dispatch = useDispatch();
  const [contentType, setContentType] = useState("DOCUMENT");
  const [isUploadingDocument, setUploadingDocument] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [failedUpload, setFailedUpload] = useState(null); // { file, name } after a failure
  const [isUploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [thumbnailUploadPct, setThumbnailUploadPct] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const thumbnailInputRef = useRef();
  // Controller for the in-flight upload, so Cancel can abort it.
  const uploadAbortControllerRef = useRef(null);

  const { fileUploaded } = useSelector((store) => store.adminpage);
  const { flagUser } = useSelector((store) => store.user);
  const thumbnail = useSelector((state) => state.images.images["thumbnail"]);
  const isDarkTheme = useSelector((state) => state.theme);
  const getThemeColor = (lightColor, darkColor) =>
    isDarkTheme ? darkColor : lightColor;

  const dropzoneStyle = {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "8px",
    border: "1px dashed #D7E5F8",
    padding: "24px",
    background: useThemeRender("insideCardBackgroundColorLayer3"),
    width: { xs: "100%", sm: "50%" },
  };

  const handlePreview = async () => {
    // Previously this only worked for PDFs and relied on the browser still having the file
    // in memory. Now it downloads the actual saved file from the server, so it works for
    // every file type.
    if (!fileDocument?.fileId) return;
    await downloadFileToLocal(fileDocument.fileId, true);
  };

  const handleLinkChange = (e) => {
    const newLink = e.target.value;
    setFormData((prevData) => ({
      ...prevData,
      attachmentLinkUrl: newLink,
      attachmentFileId: 0,
    }));
    if (newLink) {
      dispatch(setDisableNextButton(false));
    } else {
      dispatch(setDisableNextButton(true));
    }
  };

  useEffect(() => {
    if (!hasContentTypeSyncEffect) return;
    if (contentType === "DOCUMENT") {
      if (!fileDocument || Object?.keys(fileDocument)?.length === 0) {
        dispatch(setFileUploaded(false));
        dispatch(setFileDocumentAction(null));
      } else {
        dispatch(setDisableNextButton(false));
        dispatch(setFileUploaded(true));
        dispatch(setFileUploadPageName(fileUploadPageName));
      }
    } else if (contentType === "LINK") {
      if (
        formData?.attachmentLinkUrl &&
        formData?.attachmentLinkUrl?.trim() !== ""
      ) {
        dispatch(setDisableNextButton(false));
      } else {
        dispatch(setDisableNextButton(true));
      }
    }

    console.log(formData, "contentType");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentType]);

  const onDrop = async (acceptedFiles) => {
    const MAX_FILE_SIZE_MB = flagUser?.maxFileSize ?? 250;
    const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

    const file = acceptedFiles[0];
    if (!file) return;

    if (file?.size > MAX_FILE_SIZE_BYTES) {
      showErrorToast(
        `File size exceeds the ${MAX_FILE_SIZE_MB} MB limit. Please upload a smaller file.`
      );
      return;
    }
    setFailedUpload(null);
    setUploadPct(0);
    setUploadingDocument(true);
    const abortController = new AbortController();
    uploadAbortControllerRef.current = abortController;

    try {
      // Step 1: Initiate upload
      const isVideo = file.type.startsWith("video/");
      setFileType(isVideo ? "VIDEO" : "DOCUMENT");
      const { data } = await uploadYearlyReviewArtificatsFiles(
        acceptedFiles,
        false,
        flagUser?.maxFileSize,
        {
          onProgress: (done, total) =>
            setUploadPct(total ? Math.round((done / total) * 100) : 0),
          signal: abortController.signal,
        }
      );
      const fileId = data.fileId;
      dispatch(
        setFileDocumentAction({
          name: file?.name,
          type: file?.type,
          file: file,
          fileId: fileId,
        })
      );
      setFormData((prevData) => ({
        ...prevData,
        attachmentFileId: parseFileIdAsInt ? parseInt(fileId) : fileId,
        attachmentLinkUrl: "",
      }));
      dispatch(setDisableNextButton(false));
      dispatch(setFileUploaded(true));
      dispatch(setFileUploadPageName(fileUploadPageName));
    } catch (error) {
      // Canceling isn't a failure — skip the retry prompt and error log.
      if (!error?.canceled) {
        // Keep the file so the user can retry without re-selecting (upload resumes).
        setFailedUpload({ file, name: file?.name });
        console.error("Error uploading file:", error);
      }
    } finally {
      setUploadingDocument(false);
      uploadAbortControllerRef.current = null;
    }
  };

  const handleCancelUpload = () => {
    uploadAbortControllerRef.current?.abort();
  };

  const handleRetryUpload = () => {
    if (!failedUpload?.file) return;
    const fileToRetry = failedUpload.file;
    setFailedUpload(null);
    void onDrop([fileToRetry]); // resumes from already-uploaded chunks via /status
  };

  useEffect(() => {
    if (fileUploaded) {
      dispatch(setDisableNextButton(false));
    } else {
      dispatch(setDisableNextButton(true));
    }
    if (logFileUploadedEffect) {
      console.log(fileUploaded, "fileUploadedfileUploaded");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileUploaded]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (files) => {
      void onDrop(files); // Sonar-safe
    },
    disabled: isUploadingDocument, // block picking/dropping a new file mid-upload
    "application/pdf": [],
    "video/*": [],
  });

  const handleThumbnailUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadingThumbnail(true);
    setThumbnailUploadPct(0);

    try {
      // `uploadThumbnailAndGiveFileId` normalizes a single file to an array internally,
      // so passing it wrapped here is equivalent to passing it bare.
      const fileIds = await uploadThumbnailAndGiveFileId(
        [file],
        thumbnailValidation,
        setThumbnailUploadPct
      );
      const fileUrl = URL.createObjectURL(file);
      dispatch(setImage({ id: "thumbnail", url: fileUrl }));
      setFormData((prevData) => ({
        ...prevData,
        thumbnailFileId: fileIds[0],
      }));
    } catch (error) {
      // Handle error cases
      console.error(
        "Error uploading file:",
        error.response?.data || error.message
      );
    } finally {
      setUploadingThumbnail(false);
    }
  };

  const handleDocumentDelete = async (fileId) => {
    if (!fileId) {
      console.error("File ID is required to delete the file.");
      return;
    }

    try {
      if (awaitDocumentDelete) {
        await deleteCuratorFile(fileId);
      } else {
        deleteCuratorFile(fileId);
      }

      dispatch(setFileUploaded(false));
      dispatch(setFileDocumentAction(null));
    } catch (error) {
      console.error("Error deleting the file:", error);
    }
  };

  return {
    contentType,
    setContentType,
    isUploadingDocument,
    uploadPct,
    failedUpload,
    setFailedUpload,
    isUploadingThumbnail,
    thumbnailUploadPct,
    isDragging,
    setIsDragging,
    thumbnailInputRef,
    fileUploaded,
    thumbnail,
    isDarkTheme,
    getThemeColor,
    dropzoneStyle,
    handlePreview,
    handleLinkChange,
    onDrop,
    handleCancelUpload,
    handleRetryUpload,
    getRootProps,
    getInputProps,
    isDragActive,
    handleThumbnailUpload,
    handleDocumentDelete,
  };
};
